/**
 * Badge Component
 * A small status indicator component with multiple variants.
 * @packageDocumentation
 */

import { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/** Badge style variants using class-variance-authority. */
const badgeVariants = cva(
  'inline-flex items-center h-5 rounded-md border px-2.5 text-[11px] font-medium tracking-normal transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    defaultVariants: {
      variant: 'default',
    },
    variants: {
      variant: {
        outline: 'text-foreground border-border',
        info: 'border-transparent bg-info/10 text-info dark:bg-info/20',
        neutral: 'border-transparent bg-muted/80 text-muted-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        default: 'border-transparent bg-primary text-primary-foreground shadow-sm',
        success: 'border-transparent bg-success/10 text-success dark:bg-success/20',
        warning: 'border-transparent bg-warning/10 text-warning dark:bg-warning/20',
        destructive: 'border-transparent bg-destructive text-destructive-foreground shadow',
        error: 'border-transparent bg-destructive/10 text-destructive dark:bg-destructive/20',
        live: 'border-transparent bg-success/10 text-success dark:bg-success/20 animate-pulse-glow',
      },
    },
  },
);

/** Dot color mapping by badge variant. */
const dotColorMap: Record<string, string> = {
  info: 'bg-info',
  live: 'bg-success',
  success: 'bg-success',
  warning: 'bg-warning',
  default: 'bg-primary',
  error: 'bg-destructive',
  outline: 'bg-foreground',
  destructive: 'bg-destructive',
  neutral: 'bg-muted-foreground',
  secondary: 'bg-secondary-foreground',
};

/**
 * Props for the Badge component.
 */
export interface BadgeProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  /** Show a small colored dot before the text. */
  dot?: boolean;
}

/**
 * Badge component for displaying status indicators.
 * Uses forwardRef to support Radix UI asChild pattern.
 */
const Badge = forwardRef<HTMLDivElement, BadgeProps>(({ dot, variant, children, className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        badgeVariants({
          variant,
        }),
        className,
      )}
      {...props}
    >
      {dot && (
        <span className={cn('mr-1.5 inline-block size-1.5 shrink-0 rounded-full', dotColorMap[variant ?? 'default'])} />
      )}
      {children}
    </div>
  );
});
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
